import styled, { css, keyframes, type DefaultTheme } from 'styled-components';

type Tone = 'idle' | 'running' | 'done' | 'warning';

interface ShellProps {
  $tone: Tone;
}

// Matches the design's `@keyframes ns-spin` at the canonical 800ms
// linear cadence (`.ns-node__run-btn-spin`, styles.css L15045). Mirrors
// the loading-arc treatment the rest of the editor uses for in-flight
// affordances — we keep the static MdStop glyph for cancel intent.
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/**
 * Wrapper that tints the header IconButton per run-tone. Mirrors the
 * design's `.ns-node__run-btn.is-{idle,running,done,warn}` rules — the
 * IconButton itself stays generic (ghost surface), and the per-tone
 * colour swatch is projected onto its glyph + halo via this shell.
 *
 * Hover treatment is a tinted halo, not a hard background swap, so the
 * card's own state-ring (running pulse, runDone green, invalid red) is
 * never visually overpowered by the button.
 */
export const HeaderRunButtonShell = styled.span<ShellProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  ${({ $tone, theme }) => toneTint($tone, theme)}

  & > button {
    color: inherit;
  }

  & > button:hover:not(:disabled) {
    background: color-mix(in oklch, currentColor 14%, transparent);
  }

  & > button:disabled {
    opacity: 0.85;
  }
`;

/**
 * Composed running-state icon: a centred MdStop glyph ringed by a
 * rotating arc. The wrapper sets a fixed 20×20 box so the spinner has a
 * stable orbit regardless of which icon size the parent IconButton
 * uses, and the stop glyph is centred via flex inside.
 *
 * Deliberate deviation from the design (which renders the spinner ALONE
 * with no glyph): keeping the stop glyph telegraphs "click to cancel"
 * — the spinner alone leaves the user guessing whether the button is
 * still interactive while the run is in flight.
 */
export const RunningIconStack = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;

  & > svg {
    width: 14px;
    height: 14px;
    display: block;
  }
`;

/**
 * Decorative arc that rings the stop glyph while a run is in flight.
 * Single-colour top-arc + transparent rest creates the classic
 * three-quarter-empty loading look as it rotates. Marked `aria-hidden`
 * in the JSX — the spinner is purely visual; the IconButton's
 * `aria-label` already announces "Stop run".
 */
export const SpinnerRing = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: currentColor;
  animation: ${spin} 800ms linear infinite;
  pointer-events: none;

  /* Respect prefers-reduced-motion: freeze the arc at 12 o'clock so the
     button still telegraphs "running" via the static arc + stop glyph,
     without spinning at users with vestibular sensitivities. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const toneTint = ($tone: Tone, theme: DefaultTheme) => {
  const colors = theme.colors ?? {};
  switch ($tone) {
    case 'running':
      return css`
        color: ${colors.onSurface ?? '#E6E6F2'};
      `;
    case 'done':
      return css`
        color: ${colors.success ?? '#8CD88C'};
      `;
    case 'warning':
      return css`
        color: ${colors.caution ?? '#E6C76A'};
      `;
    case 'idle':
    default:
      return css`
        color: ${colors.primary ?? '#C1C1FF'};
      `;
  }
};
